import { useId } from "@fluentui/react-hooks";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { IChoiceGroupOption, IChoiceGroupOptionProps } from "@fluentui/react/lib/ChoiceGroup";
import { Stack } from "@fluentui/react/lib/Stack";
import { IRenderFunction } from "@fluentui/react/lib/Utilities";

import { ItemKeyValueGrid } from "Components/Generic/KeyValueGrid/ItemKeyValueGrid";
import { Teach } from "Components/Generic/Teach";
import { IAddressValidationResult } from "Model/Omikron/IAddressValidationResult";
import { IItem } from "Model/Storage/IItem";
import { IProject } from "Model/Storage/IProject";
import { isTrue } from "Utils/Boolean";
import { getItemType } from "Utils/Item";
import { WithProjectItemContext } from "../../ProjectItemContext/WithProjectItemContext";
import { useChoiceStyles } from "./UseChoiceStyles";
import { IChoiceProps } from "./IChoiceProps";
import { ResultCard } from "./ResultCard";
import { useChoice } from "./UseChoice";

const Choice = WithProjectItemContext((props: IChoiceProps): JSX.Element => {
  const { result } = props;
  const {
    cachedProjectItem, hasAction, hasIsRelevantIcon, isSelectedResult, items, onActionClick, onIsRelevantClick
  } = useChoice(props);
  const { choiceStyles, gridStyles } = useChoiceStyles();
  const actionButtonId = useId(`actionButton`);
  const actionStyle = hasAction ? choiceStyles.primaryAction : choiceStyles.secondaryAction;
  const actionAlign = hasAction ? `stretch` : `start`;

  return (
    <Stack horizontal className={choiceStyles.root}>
      <Stack.Item grow className={choiceStyles.result}>
        <ResultCard result={result}>
          <ItemKeyValueGrid entityType={getItemType(cachedProjectItem)} items={items} styleSet={gridStyles} />
        </ResultCard>
      </Stack.Item>
      <Stack.Item id={actionButtonId} align={actionAlign} disableShrink className={actionStyle}>
        {hasIsRelevantIcon
          ? (
            <DefaultButton
              onClick={onIsRelevantClick}
              iconProps={{
                iconName: isTrue(cachedProjectItem?.[`@isRelevant`] ?? result?.[`@isRelevant`])
                  ? `FavoriteStarFill`
                  : `FavoriteStar`
              }}
            />
          )
          : null}
        {hasAction
          ? (
            <>
              <Teach id={actionButtonId} token="dc.merge.teach" show={isSelectedResult} />
              <DefaultButton onClick={onActionClick} iconProps={{ iconName: `ChevronRightMed` }} />
            </>
          )
          : null}
      </Stack.Item>
    </Stack>
  );
});

export function generateOnRenderLabel(
  result: IAddressValidationResult,
  cache: IProject,
  onAction: (item: IItem) => void,
  onIsRelevant: (projectItemId: string) => void
): IRenderFunction<IChoiceGroupOption & IChoiceGroupOptionProps> {
  return () => <Choice result={result} cache={cache} onAction={onAction} onIsRelevant={onIsRelevant} />;
}
